import { Alert, Button } from '@mui/material';
import { GoogleLogin } from '@react-oauth/google';
import { checkAuthPolicy } from 'client/modules/Auth/AuthActions';
import FormsyPassword from 'components/FormsyPassword/FormsyPassword';
import FormsyText from 'components/FormsyText/FormsyText';
import Message from 'components/Message/Message';
import PasswordStrengthIndicator, {
  validatePassword,
} from 'components/PasswordStrengthIndicator/PasswordStrengthIndicator';
import Formsy from 'formsy-react';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import styles from './LoginWidget.css';

export const loginMessages = defineMessages({
  'Signup.Title': {
    id: 'Signup.Title',
    defaultMessage: 'Sign up',
  },
  'Signup.Submit': {
    id: 'Signup.Submit',
    defaultMessage: 'Create my account',
  },
  'Login.Title': {
    id: 'Login.Title',
    defaultMessage: 'Log in',
  },
  'Login.Submit': {
    id: 'Login.Submit',
    defaultMessage: 'Log in',
  },
  email: {
    id: 'Login.Email',
    defaultMessage: 'name@company.com',
  },
  password: {
    id: 'Login.Password',
    defaultMessage: 'password',
  },
  submit: {
    id: 'Login.Submit',
    defaultMessage: 'Log in',
  },
  invalidEmail: {
    id: 'Login.Errors.InvalidEmail',
    defaultMessage: 'This is not a valid email',
  },
  or: {
    id: 'Login.Or',
    defaultMessage: 'or',
  },
  ssoEnforced: {
    id: 'Login.SSOEnforced',
    defaultMessage: 'This organization requires single sign-on. Continue with {providers}.',
  },
  ssoRequiredError: {
    id: 'Login.SSORequiredError',
    defaultMessage: 'This organization requires single sign-on. Sign in with {providers}.',
  },
  yourSSOProvider: {
    id: 'Login.YourSSOProvider',
    defaultMessage: 'your SSO provider',
  },
  deprecatedAccount: {
    id: 'Login.DeprecatedAccount',
    defaultMessage:
      'Access with this email has been disabled. Please use your corporate account to sign in via {providers}.',
  },
});

// Helpers to format provider ids like ["google", "microsoft", "scim"] → "Google, Microsoft, or SCIM"
const PROVIDER_DISPLAY_NAMES = {
  scim: 'SCIM',
  github: 'GitHub',
  google: 'Google',
  microsoft: 'Microsoft',
  okta: 'Okta',
};

function toProviderDisplayName(providerId) {
  if (!providerId || typeof providerId !== 'string') return '';
  const normalized = providerId.trim().toLowerCase();
  if (PROVIDER_DISPLAY_NAMES[normalized]) return PROVIDER_DISPLAY_NAMES[normalized];
  return normalized
    .split(/[-_ ]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatProviderListForLocale(providerIds, locale = 'en') {
  if (!providerIds || !Array.isArray(providerIds) || providerIds.length === 0) return null;
  const names = providerIds.map(toProviderDisplayName).filter(Boolean);
  if (names.length === 0) return '';
  const formatter = new Intl.ListFormat(locale, { style: 'long', type: 'disjunction' });
  return formatter.format(names);
}

const LoginWidget = props => {
  const email = useRef();
  const password = useRef();
  const { formatMessage, locale } = useIntl();
  const [passwordValue, setPasswordValue] = useState('');
  const dispatch = useDispatch();

  const { errorMessage, isSignup, onLoginError } = props;
  const [enforcedSSO, setEnforcedSSO] = useState(false);
  const [reason, setReason] = useState('');
  const [providers, setProviders] = useState([]);

  const providersText = useMemo(
    () =>
      formatProviderListForLocale(providers, locale || 'en') ||
      formatMessage(loginMessages.yourSSOProvider),
    [providers, formatMessage],
  );

  const handleSubmit = () => {
    if (enforcedSSO) {
      onLoginError(
        formatMessage(
          reason === 'Deprecated account'
            ? loginMessages.deprecatedAccount
            : loginMessages.ssoRequiredError,
          { providers: providersText },
        ),
      );
      return;
    }
    // Only check password strength for signup
    if (props.isSignup && !validatePassword(passwordValue, formatMessage).isStrong) {
      onLoginError(
        formatMessage({
          id: 'AuthError.WeakPasswordSignup',
          defaultMessage: 'Password is too weak. Please ensure it meets all requirements.',
        }),
      );
      return;
    }

    const creds = {
      email: email.current.getValue().trim().toLowerCase(),
      password: password.current.getValue().trim(),
    };
    props.onLoginClick(creds, props.isSignup);
  };

  const handlePasswordChange = useCallback((name, value) => {
    setPasswordValue(value);
  }, []);

  const onGoogleSignIn = response => {
    props.onLoginClick({ credential: response.credential }, props.isSignup);
  };
  const onGoogleSignInError = error => {
    onLoginError(error.error);
  };

  // Check SSO policy on email blur
  const onEmailBlur = async () => {
    try {
      const value = email.current?.getValue?.();
      if (!value) return;
      const json = await dispatch(checkAuthPolicy(value));
      setEnforcedSSO(!!json.enforced);
      setProviders(json.providers || []);
      setReason(json.reason);
    } catch (e) {}
  };

  const buttonLabel = props.isSignup
    ? formatMessage(loginMessages['Signup.Submit'])
    : formatMessage(loginMessages['Login.Submit']);

  return (
    <div className={styles.container}>
      <Message
        type="error"
        msg={errorMessage}
        onClose={props.onCloseError}
        className={`${styles.msg} ${errorMessage ? styles.visible : styles.hidden}`}
      />
      <Formsy.Form onValidSubmit={handleSubmit} className={styles.form}>
        <FormsyText
          id="email"
          name="email"
          validations="isEmail"
          ref={email}
          validationError={formatMessage(loginMessages.invalidEmail)}
          floatingLabelText={formatMessage(loginMessages.email)}
          required
          autoFocus
          fullWidth
          onBlur={onEmailBlur}
        />
        {!enforcedSSO && (
          <FormsyPassword
            id="password"
            name="password"
            type="password"
            ref={password}
            floatingLabelText={formatMessage(loginMessages.password)}
            required
            fullWidth
            onChange={handlePasswordChange}
          />
        )}

        {/* Password Strength Indicator - Only for Signup */}
        {isSignup && !enforcedSSO ? (
          <PasswordStrengthIndicator password={passwordValue} marginTop={1} marginBottom={2} />
        ) : (
          <Link className={styles.forgot} to="/forgot-password">
            <FormattedMessage id="Login.ForgotPassword" defaultMessage="Forgot your password?" />
          </Link>
        )}
        {!enforcedSSO && (
          <Button
            variant="contained"
            className={styles.submit}
            type="submit"
            color="primary"
            style={{ width: '100%' }}
            disabled={
              isSignup && passwordValue && !validatePassword(passwordValue, formatMessage).isStrong
            }
          >
            {buttonLabel}
          </Button>
        )}
        {enforcedSSO && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {formatMessage(
              reason === 'Deprecated account'
                ? loginMessages.deprecatedAccount
                : loginMessages.ssoEnforced,
              { providers: providersText },
            )}
          </Alert>
        )}
      </Formsy.Form>
      <hr className={styles.hrText} data-content={formatMessage(loginMessages.or)} />
      {!!process.env.CLIENT && locale && (
        <GoogleLogin
          onSuccess={onGoogleSignIn}
          onError={onGoogleSignInError}
          locale={locale}
          useOneTap
          auto_select
          cancel_on_tap_outside
          itp_support
          shape="pill"
          size="large"
          context={isSignup ? 'signup' : 'signin'}
          text={isSignup ? 'signup_with' : 'signin_with'}
        />
      )}
      {/* Terms and Privacy Agreement - Only for Signup */}
      {isSignup && (
        <div className={styles.acceptTerms}>
          <FormattedMessage
            id="Home.AgreeTerms"
            defaultMessage="By creating an account you agree to our {terms} & {privacy}."
            values={{
              terms: (
                <Link to={{ pathname: '/legal/terms' }}>
                  <FormattedMessage id="Home.Terms" defaultMessage="Terms" />
                </Link>
              ),
              privacy: (
                <Link to={{ pathname: '/legal/privacy' }}>
                  <FormattedMessage id="Home.Privacy" defaultMessage="Privacy Policy" />
                </Link>
              ),
            }}
          />
        </div>
      )}
    </div>
  );
};

LoginWidget.defaultProps = {
  errorMessage: null,
};

LoginWidget.propTypes = {
  onLoginClick: PropTypes.func.isRequired,
  errorMessage: PropTypes.shape({
    id: PropTypes.string,
  }),
  isSignup: PropTypes.bool.isRequired,
  onCloseError: PropTypes.func.isRequired,
  onLoginError: PropTypes.func.isRequired,
};

export default LoginWidget;
